import AppContext from 'App/AppContext';
import {
  IPDefaultButton,
  IPIconButton,
  IPModal,
  IPPrimaryButton,
  IPSeparator,
  IPSpinner,
  IPText,
  IPTooltipHost,
} from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import WarningMessage from 'components/Notification/WarningMessage';
import {
  globalStackStylesHeight100PaddingSmall,
  globalStackTokensGapSmall,
  refreshIcon,
  sharepointIcon,
} from 'globalStyles';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GraphScopeId } from 'services/Auth/authConfig';
import { getGraphSharepointErrorMsg, toAppError } from 'services/Graph/graphErrors';
import { graphGetPagesForSite } from 'services/Graph/graphService';
import { navigateToExternalUrl } from 'utils/url';
import { ISite, ISitePage } from '../../services/Graph/SharepointInterfaces';
import DirectoryParents from './DirectoryParents';
import MySitePages from './MySitePages';
import MySites from './MySites';
import { IPSpinnerSize } from "baseComponents/Spinner/IPSpinner";

interface ISharepointSitePickerProps {
  isOpen: boolean;
  hideContent?: boolean;
  close: (item: ISite | undefined) => void;
  onSelect: (item: ISite) => void;
}

const SharepointSitePicker = (props: ISharepointSitePickerProps) => {
  const [selectedSite, setSelectedSite] = useState<ISite | undefined>(undefined);
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [selectedSitePage, setSelectedSitePage] = useState<ISitePage | undefined>(undefined);
  const [sitePages, setSitePages] = useState<ISitePage[]>([]);
  const [spError, setSPError] = useState<string | undefined>(undefined);

  const appContext = useContext(AppContext);

  const { t } = useTranslation(['sharepoint', 'translation']);

  const checkToken = (token: string): boolean => {
    if (!token) {
      appContext.showNotification(t('translation:General.Notifications.TryAgainAfterConsent'), true);

      return false;
    }

    return true;
  };

  const fetchPages = async () => {
    if (isBusy || selectedSite === undefined || !selectedSite.id) return;
    try {
      setIsBusy(true);
      setSPError(undefined);

      const graphInterface = await appContext.getGraphInterface(GraphScopeId.Pages, selectedSite.tenantId);
      if (!checkToken(graphInterface.accessToken)) return;
      const _pages: ISitePage[] = await graphGetPagesForSite(graphInterface.client, selectedSite.id, false);
      _pages.forEach((l) => (l.tenantId = selectedSite.tenantId));
      setSitePages(_pages);
    } catch (err) {
      const graphErrorMsg = getGraphSharepointErrorMsg(err, t);
      if (graphErrorMsg) {
        setSPError(graphErrorMsg);
      } else {
        appContext.setError(toAppError(err));
      }
    } finally {
      setIsBusy(false);
    }
  };

  const updateSiteSelection = (site?: ISite) => {
    setSelectedSite(site);
    setSelectedSitePage(undefined);
    setSitePages([]);
  };

  useEffect(() => {
    fetchPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite]);

  const onRefresh = () => {
    if (selectedSite) {
      fetchPages();
    }
  };

  const onOpenSharePoint = () => {
    if (!selectedSite?.webUrl) return;
    navigateToExternalUrl(selectedSite.webUrl + '/SitePages', undefined, '');
  };

  //
  // Main render
  //
  return (
    <IPModal isOpen={props.isOpen} onDismiss={() => props.close(selectedSite)} isBlocking>
      <IPStack
        verticalFill
        styles={{
          root: { height: '80vh', width: props.hideContent ? 500 : 1000, minWidth: 320, maxWidth: '90vw', padding: 20 },
        }}
      >
        {/* Title and Close button*/}
        <IPStack horizontal styles={{ root: { justifyContent: 'space-between' } }}>
          <IPStack.Item>
            <IPText variant="xLarge">{t('sharepoint:SharepointPicker.SelectSite')}</IPText>
          </IPStack.Item>
          <IPStack.Item>
            <IPIconButton iconProps={{ iconName: 'CalculatorMultiply' }} onClick={() => props.close(selectedSite)} />
          </IPStack.Item>
        </IPStack>
        <IPSeparator />
        <IPStack verticalFill>
          {/* Body Left and Right*/}
          <IPStack.Item grow>
            <IPStack horizontal styles={globalStackStylesHeight100PaddingSmall} tokens={globalStackTokensGapSmall}>
              {/* Left Groups Part*/}
              <IPStack.Item styles={{ root: { width: props.hideContent ? '100%' : '30%', padding: 5 } }}>
                <MySites
                  onSelect={(item) => updateSiteSelection(item)}
                  selectedSite={selectedSite}
                  checkToken={checkToken}
                  authType={GraphScopeId.Pages}
                />
                <IPSeparator vertical />
              </IPStack.Item>
              {/* Right Part*/}
              {!props.hideContent && (
                <IPStack.Item grow styles={{ root: { width: '70%', padding: 5 } }}>
                  <IPStack verticalFill>
                    {selectedSite && (
                      <IPStack.Item>
                        <IPStack horizontal wrap horizontalAlign="space-between">
                          <IPStack.Item>
                            <DirectoryParents
                              drive={selectedSitePage}
                              parents={[]}
                              site={selectedSite}
                              onSelectDrive={() => {}}
                              onSelectDriveFolder={() => {}}
                              onBack={() => {}}
                              onSelectSite={updateSiteSelection}
                            />
                          </IPStack.Item>
                          <IPStack.Item>
                            {selectedSite && (
                              <IPTooltipHost content={t('sharepoint:CommandBar.OpenSharePoint')}>
                                <IPIconButton iconProps={sharepointIcon} onClick={onOpenSharePoint} />
                              </IPTooltipHost>
                            )}
                            <IPTooltipHost content={t('sharepoint:CommandBar.Refresh')}>
                              <IPIconButton iconProps={refreshIcon} onClick={onRefresh} />
                            </IPTooltipHost>
                          </IPStack.Item>
                        </IPStack>
                      </IPStack.Item>
                    )}
                    {!selectedSitePage && !selectedSite && isBusy && (
                      <IPStack verticalFill verticalAlign="center">
                        <IPSpinner size={IPSpinnerSize.large} />
                      </IPStack>
                    )}
                    {spError && !isBusy && (
                      <IPStack verticalFill>
                        <WarningMessage message={spError} onDismiss={undefined}></WarningMessage>
                      </IPStack>
                    )}
                    {!spError && selectedSite && selectedSitePage === undefined && (
                      <IPStack.Item grow>
                        <MySitePages pages={sitePages} isListLoading={isBusy} existingLinks={[]} />
                      </IPStack.Item>
                    )}
                  </IPStack>
                </IPStack.Item>
              )}
            </IPStack>
          </IPStack.Item>
        </IPStack>
        <IPSeparator />
        <IPStack horizontalAlign="end" horizontal tokens={globalStackTokensGapSmall}>
          <IPPrimaryButton
            text={t('translation:General.Button.Select')}
            disabled={selectedSite === undefined}
            onClick={() => {
              if (selectedSite) {
                props.onSelect(selectedSite);
                props.close(selectedSite);
              }
            }}
          />
          <IPDefaultButton text={t('translation:General.Button.Cancel')} onClick={() => props.close(selectedSite)} />
        </IPStack>
      </IPStack>
    </IPModal>
  );
};

export default SharepointSitePicker;
